import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error("Falha ao validar permissão");
  if (!data?.role) throw new Error("Forbidden: apenas admins");
}

/**
 * Coleta títulos, aberturas e perguntas de FAQ já usados em páginas locais
 * do mesmo serviço, para alimentar o prompt e forçar conteúdo original
 * (evitando penalização do Google por conteúdo duplicado).
 */
async function fetchLocationAvoidSets(
  supabase: any,
  serviceSlug: string,
): Promise<{ titles: string[]; snippets: string[]; faqs: string[] }> {
  try {
    const { data } = await supabase
      .from("service_locations")
      .select("title, body")
      .eq("service_slug", serviceSlug)
      .order("created_at", { ascending: false })
      .limit(30);
    const rows = (data ?? []) as Array<{ title: string | null; body: string | null }>;
    const titles: string[] = [];
    const snippets: string[] = [];
    const faqs: string[] = [];
    for (const r of rows) {
      if (r.title) titles.push(r.title);
      const body = r.body ?? "";
      // First non-empty paragraph (skip headings/lists) as an "opening" sample.
      const firstPara = body
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .find((p) => p && !p.startsWith("#") && !p.startsWith("-") && !p.startsWith("*"));
      if (firstPara) snippets.push(firstPara.slice(0, 220));
      // FAQ questions are H3 lines under the FAQ section.
      const faqIdx = body.search(/##\s+FAQ/i);
      if (faqIdx >= 0) {
        const tail = body.slice(faqIdx);
        const qs = Array.from(tail.matchAll(/^###\s+(.+)$/gm)).map((m) => m[1].trim());
        faqs.push(...qs);
      }
    }
    return { titles, snippets, faqs };
  } catch {
    return { titles: [], snippets: [], faqs: [] };
  }
}

export const ARTICLE_TYPES = ["info", "review", "comparativo", "melhores", "empresa"] as const;
const articleTypeSchema = z.enum(ARTICLE_TYPES).default("info");

const productFieldsSchema = {
  product_name: z.string().trim().max(200).nullable().optional(),
  product_image: z.string().trim().max(800).nullable().optional(),
  product_image_alt: z.string().trim().max(200).nullable().optional(),
  product_url: z.string().trim().max(800).nullable().optional(),
  product_price: z.string().trim().max(60).nullable().optional(),
  product_rating: z.number().min(0).max(5).nullable().optional(),
  product_reviews_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
  product_features: z.array(z.string().trim().min(1).max(200)).max(12).default([]),
  product_cta_label: z.string().trim().max(60).nullable().optional(),
  product_marketplace: z.string().trim().max(60).nullable().optional(),
  product_cta_bg_color: z.string().trim().max(30).nullable().optional(),
  product_cta_text_color: z.string().trim().max(30).nullable().optional(),
};
const extraProductItemSchema = z.object(productFieldsSchema);
const productSchema = {
  ...productFieldsSchema,
  extra_products: z.array(extraProductItemSchema).max(8).default([]),
};

// --- Business (empresa) profile schema — used by "empresa" article type ---
const partnerSchema = z.object({
  name: z.string().trim().max(120).default(""),
  description: z.string().trim().max(400).default(""),
  logo: z.string().trim().max(800).default(""),
  website: z.string().trim().max(400).default(""),
  whatsapp: z.string().trim().max(40).default(""),
});
const businessDataSchema = z
  .object({
    nap: z
      .object({
        name: z.string().trim().max(200).default(""),
        address: z.string().trim().max(400).default(""),
        phone: z.string().trim().max(40).default(""),
        hours: z.string().trim().max(200).default(""),
      })
      .default({ name: "", address: "", phone: "", hours: "" }),
    description: z.string().trim().max(2000).default(""),
    socials: z
      .object({
        instagram: z.string().trim().max(400).default(""),
        facebook: z.string().trim().max(400).default(""),
        linkedin: z.string().trim().max(400).default(""),
        youtube: z.string().trim().max(400).default(""),
        website: z.string().trim().max(400).default(""),
      })
      .default({ instagram: "", facebook: "", linkedin: "", youtube: "", website: "" }),
    google_maps_embed: z.string().trim().max(4000).default(""),
    // `partners` legado — aceito para compatibilidade de dados antigos, mas não é
    // mais exposto na UI nem renderizado no post.
    partners: z.array(partnerSchema).max(20).default([]).optional(),
  })
  .nullable()
  .optional();

const postInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().min(2).max(120).regex(/^[a-z0-9-]+$/, "Use apenas letras minúsculas, números e hífens"),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().min(10).max(300),
  excerpt: z.string().trim().max(400).default(""),
  category: z.string().trim().min(1).max(60),
  cover: z.string().trim().max(400).default("linear-gradient(135deg,#005BFF,#0a1a3a)"),
  body: z.string().max(50000).default(""),
  author: z.string().trim().min(1).max(120).default("Rafael Santos"),
  reading_time: z.string().trim().max(20).default("5 min"),
  published: z.boolean().default(false),
  cover_image: z.string().trim().max(800).nullable().optional(),
  youtube_urls: z.array(z.string().trim().url().max(400)).max(50).default([]),
  keywords: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  article_type: articleTypeSchema,
  ...productSchema,
  business_data: businessDataSchema,
});

export const isAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    return { admin: Boolean(data?.role), userId: context.userId };
  });

export const listAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("posts")
      .select("id, slug, title, category, published, published_at, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getPostForEdit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: post, error } = await context.supabase
      .from("posts")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return post;
  });

export const savePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => postInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      slug: data.slug,
      title: data.title,
      description: data.description,
      excerpt: data.excerpt,
      category: data.category,
      cover: data.cover,
      body: data.body,
      author: data.author,
      reading_time: data.reading_time,
      published: data.published,
      published_at: data.published ? new Date().toISOString() : null,
      created_by: context.userId,
      cover_image: data.cover_image ?? null,
      youtube_urls: data.youtube_urls,
      keywords: data.keywords,
      article_type: data.article_type,
      product_name: data.product_name ?? null,
      product_image: data.product_image ?? null,
      product_image_alt: data.product_image_alt ?? null,
      product_url: data.product_url ?? null,
      product_price: data.product_price ?? null,
      product_rating: typeof data.product_rating === "number" ? data.product_rating : null,
      product_reviews_count: typeof data.product_reviews_count === "number" ? data.product_reviews_count : null,
      product_features: data.product_features ?? [],
      product_cta_label: data.product_cta_label ?? null,
      product_marketplace: data.product_marketplace ?? null,
      product_cta_bg_color: data.product_cta_bg_color ?? null,
      product_cta_text_color: data.product_cta_text_color ?? null,
      business_data: data.business_data ?? null,
    };
    if (data.id) {
      const { data: row, error } = await context.supabase
        .from("posts")
        .update(payload)
        .eq("id", data.id)
        .select("id, slug")
        .single();
      if (error) throw new Error(error.message);
      return row;
    }
    const { data: row, error } = await context.supabase
      .from("posts")
      .insert(payload)
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── CRM Suporte (instruções da IA + sincronização da playlist) ─────────

export const getCrmSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("crm_settings")
      .select("support_instructions, playlist_url, auto_sync, last_sync_at, last_sync_count, last_sync_error")
      .eq("singleton", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const saveCrmSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      support_instructions: z.string().max(8000).default(""),
      playlist_url: z.string().trim().url().max(500),
      auto_sync: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("crm_settings")
      .update({
        support_instructions: data.support_instructions,
        playlist_url: data.playlist_url,
        auto_sync: data.auto_sync,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("singleton", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listCrmVideos = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("crm_videos")
      .select("id, title, category, keywords, position, hidden, updated_at")
      .order("position", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const deleteCrmVideo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().min(5).max(20) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("crm_videos").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const syncCrmVideos = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { syncCrmPlaylist } = await import("@/lib/crm-playlist-sync.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const result = await syncCrmPlaylist(supabaseAdmin);
    return result;
  });

// --- Keywords ---

export const listKeywords = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const select = "id, keyword, topic, active, last_used_at, usage_count, article_type, product_name, product_image, product_image_alt, product_url, product_price, product_rating, product_reviews_count, product_features, product_cta_label, product_marketplace, product_cta_bg_color, product_cta_text_color, extra_products, business_data, scheduled_for, repeat_interval_hours, keyword_variations, scheduled_dates, variation_index";
    const pageSize = 1000;
    const rows: any[] = [];

    for (let from = 0; ; from += pageSize) {
      const { data, error } = await context.supabase
        .from("blog_keywords")
        .select(select)
        .order("last_used_at", { ascending: true, nullsFirst: true })
        .order("keyword", { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw new Error(error.message);

      const page = data ?? [];
      rows.push(...page);
      if (page.length < pageSize) break;
    }

    return rows;
  });

export const saveKeyword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      keyword: z.string().trim().min(2).max(120),
      topic: z.string().trim().max(400).default(""),
      active: z.boolean().default(true),
      article_type: articleTypeSchema,
      ...productSchema,
      business_data: businessDataSchema,
      scheduled_for: z.string().trim().min(1).nullable().optional(),
      repeat_interval_hours: z.number().int().min(1).max(8760).nullable().optional(),
      keyword_variations: z.array(z.string().trim().min(2).max(200)).max(100).optional(),
      scheduled_dates: z.array(z.string().trim().min(1)).max(100).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      keyword: data.keyword,
      topic: data.topic,
      active: data.active,
      article_type: data.article_type,
      product_name: data.product_name ?? null,
      product_image: data.product_image ?? null,
      product_image_alt: data.product_image_alt ?? null,
      product_url: data.product_url ?? null,
      product_price: data.product_price ?? null,
      product_rating: typeof data.product_rating === "number" ? data.product_rating : null,
      product_reviews_count: typeof data.product_reviews_count === "number" ? data.product_reviews_count : null,
      product_features: data.product_features ?? [],
      product_cta_label: data.product_cta_label ?? null,
      product_marketplace: data.product_marketplace ?? null,
      product_cta_bg_color: data.product_cta_bg_color ?? null,
      product_cta_text_color: data.product_cta_text_color ?? null,
      extra_products: data.extra_products ?? [],
      business_data: data.business_data ?? null,
      ...(data.scheduled_for === undefined
        ? {}
        : { scheduled_for: data.scheduled_for ? new Date(data.scheduled_for).toISOString() : null }),
      ...(data.repeat_interval_hours === undefined
        ? {}
        : { repeat_interval_hours: data.repeat_interval_hours ?? null }),
      ...(data.keyword_variations === undefined
        ? {}
        : { keyword_variations: data.keyword_variations }),
      ...(data.scheduled_dates === undefined
        ? {}
        : {
            scheduled_dates: (data.scheduled_dates ?? [])
              .map((s) => {
                const d = new Date(s);
                return isNaN(d.getTime()) ? null : d.toISOString();
              })
              .filter((s): s is string => !!s),
          }),
    };
    let rowId = data.id ?? null;
    if (data.id) {
      const { error } = await context.supabase.from("blog_keywords").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { data: inserted, error } = await context.supabase
        .from("blog_keywords")
        .insert(payload)
        .select("id")
        .maybeSingle();
      if (error) throw new Error(error.message);
      rowId = (inserted as any)?.id ?? null;
    }

    // Sem sincronização com cron externo: o pg_cron interno dispara o hook,
    // que processa keywords vencidas por `scheduled_for`.
    void rowId;
    return { ok: true };
  });

export const deleteKeyword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("blog_keywords").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Bulk add keywords with angles. Accepts free-form lines like "keyword | angle"
// (also accepts ";" or tab as separators). Duplicates the exact keyword are skipped.
export const addKeywordsBulk = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      items: z
        .array(
          z.object({
            keyword: z.string().trim().min(2).max(120),
            topic: z.string().trim().max(400).default(""),
            article_type: articleTypeSchema.default("info"),
          }),
        )
        .min(1)
        .max(500),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    // Skip any that already exist (case-insensitive on keyword).
    const { data: existing } = await context.supabase
      .from("blog_keywords")
      .select("keyword");
    const existingSet = new Set(
      (existing ?? []).map((e: any) => String(e.keyword ?? "").trim().toLowerCase()),
    );
    const seen = new Set<string>();
    const rows = [] as any[];
    let skipped = 0;
    for (const it of data.items) {
      const key = it.keyword.trim().toLowerCase();
      if (!key) continue;
      if (existingSet.has(key) || seen.has(key)) { skipped++; continue; }
      seen.add(key);
      rows.push({
        keyword: it.keyword.trim(),
        topic: it.topic ?? "",
        active: true,
        article_type: it.article_type ?? "info",
      });
    }
    if (rows.length === 0) return { ok: true, inserted: 0, skipped };
    const { error } = await context.supabase.from("blog_keywords").insert(rows);
    if (error) throw new Error(error.message);
    return { ok: true, inserted: rows.length, skipped };
  });

// --- Internal links ---

export const listInternalLinks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("internal_links")
      .select("id, anchor, url, active, priority")
      .order("priority", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const saveInternalLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      anchor: z.string().trim().min(2).max(120),
      url: z.string().trim().min(1).max(400)
        .regex(/^(\/[a-zA-Z0-9\-_/.?#=&%]*|https:\/\/[^\s"'<>]+)$/, {
          message: "URL deve ser um caminho relativo iniciando com / ou uma URL https://",
        }),
      active: z.boolean().default(true),
      priority: z.number().int().min(0).max(100).default(5),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = { anchor: data.anchor, url: data.url, active: data.active, priority: data.priority };
    if (data.id) {
      const { error } = await context.supabase.from("internal_links").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await context.supabase.from("internal_links").insert(payload);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deleteInternalLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("internal_links").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Bulk import of all site URLs (services, posts, locations, static pages)
// into the internal_links table, with auto-derived anchors. Existing rows
// matching (anchor + url) are kept untouched; new ones are inserted active.
export const importSitemapLinks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabase = context.supabase;

    const STATIC: Array<{ anchor: string; url: string; priority: number }> = [
      { anchor: "Tudo sobre Concurso Astor", url: "/", priority: 4 },
      { anchor: "melhores itens de do tema abordado", url: "/melhores-robos-aspiradores", priority: 5 },
      { anchor: "comparativo de itens de do tema abordado", url: "/melhores-robos-aspiradores", priority: 4 },
      { anchor: "fale conosco", url: "/contato", priority: 3 },
      { anchor: "sobre o site", url: "/sobre", priority: 3 },
      { anchor: "blog de do tema abordado", url: "/blog", priority: 2 },
    ];

    const [services, posts, locs, models] = await Promise.all([
      supabase.from("services").select("slug, label, route_path").eq("active", true),
      supabase.from("posts").select("slug, title, keywords").eq("published", true).limit(1000),
      supabase.from("service_locations").select("slug, title, service_label, city, neighborhood").eq("published", true).limit(1000),
      (supabase as any).from("product_models").select("slug, name, brand").eq("active", true).limit(1000),
    ]);

    const rows: Array<{ anchor: string; url: string; priority: number; active: boolean }> = [];
    for (const s of STATIC) rows.push({ ...s, active: true });
    for (const m of (models.data ?? []) as any[]) {
      const url = `/modelos/${m.slug}`;
      if (m.name) rows.push({ anchor: m.name, url, priority: 7, active: true });
      if (m.brand) rows.push({ anchor: m.brand, url, priority: 4, active: true });
    }
    for (const s of (services.data ?? []) as any[]) {
      const url = s.route_path || `/${s.slug}`;
      if (s.label) rows.push({ anchor: s.label, url, priority: 6, active: true });
    }
    for (const p of (posts.data ?? []) as any[]) {
      if (p.title) rows.push({ anchor: p.title, url: `/blog/${p.slug}`, priority: 5, active: true });
      for (const kw of (p.keywords ?? []) as any[]) {
        if (typeof kw === "string" && kw.length >= 3) {
          rows.push({ anchor: kw, url: `/blog/${p.slug}`, priority: 4, active: true });
        }
      }
    }
    for (const l of (locs.data ?? []) as any[]) {
      const where = l.neighborhood ? `${l.service_label} em ${l.neighborhood}` : `${l.service_label} em ${l.city}`;
      rows.push({ anchor: where, url: `/servicos/${l.slug}`, priority: 5, active: true });
      if (l.title) rows.push({ anchor: l.title, url: `/servicos/${l.slug}`, priority: 4, active: true });
    }

    const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    const seen = new Set<string>();
    const unique = rows.filter((r) => {
      const k = `${norm(r.anchor)}|${r.url}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return r.anchor.length >= 2 && r.anchor.length <= 120 && r.url.length <= 400;
    });

    const { data: existing } = await supabase.from("internal_links").select("anchor, url");
    const existingSet = new Set<string>(((existing ?? []) as any[]).map((e) => `${norm(e.anchor)}|${e.url}`));
    const toInsert = unique.filter((r) => !existingSet.has(`${norm(r.anchor)}|${r.url}`));

    if (toInsert.length === 0) return { inserted: 0, scanned: unique.length };

    let inserted = 0;
    for (let i = 0; i < toInsert.length; i += 200) {
      const chunk = toInsert.slice(i, i + 200);
      const { error } = await supabase.from("internal_links").insert(chunk);
      if (error) throw new Error(error.message);
      inserted += chunk.length;
    }
    return { inserted, scanned: unique.length };
  });

// --- Schedule settings ---

export const getScheduleSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("blog_schedule_settings")
      .select("id, enabled, frequency, system_prompt, local_seo_system_prompt, auto_publish, posts_per_run, links_per_article, schedule_times, use_link_building, interval_minutes, last_run_at, rotate_authors, default_youtube_urls, default_youtube_titles, videos_per_article, use_videos_in_schedule, image_search_prompt, youtube_channel_url, last_channel_sync_at, last_channel_sync_count, last_channel_sync_error, updated_at")
      .eq("singleton", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const saveScheduleSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      enabled: z.boolean(),
      frequency: z.enum(["daily", "weekly", "manual", "interval"]).default("daily"),
      auto_publish: z.boolean().default(true),
      posts_per_run: z.number().int().min(1).max(10).default(1),
      links_per_article: z.number().int().min(0).max(15).default(3),
      schedule_times: z.array(z.string().regex(/^\d{2}:\d{2}$/)).min(1).max(6).default(["09:00"]),
      use_link_building: z.boolean().default(true),
      interval_minutes: z.number().int().min(0).max(1440).default(0),
      rotate_authors: z.boolean().default(false),
      use_videos_in_schedule: z.boolean().default(true),
      videos_per_article: z.number().int().min(0).max(5).default(1),
      image_search_prompt: z.string().trim().max(2000).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const rawInterval = Number(data.interval_minutes ?? 5);
    const clampedInterval = Number.isFinite(rawInterval)
      ? Math.max(5, Math.min(1440, Math.round(rawInterval)))
      : 5;
    const intervalMinutes = data.frequency === "interval" ? clampedInterval : 0;
    const { error } = await context.supabase
      .from("blog_schedule_settings")
      .update({
        enabled: data.enabled,
        frequency: data.frequency,
        auto_publish: data.auto_publish,
        posts_per_run: data.posts_per_run,
        links_per_article: data.links_per_article,
        schedule_times: data.schedule_times,
        use_link_building: data.use_link_building,
        interval_minutes: intervalMinutes,
        rotate_authors: data.rotate_authors,
        use_videos_in_schedule: data.use_videos_in_schedule,
        videos_per_article: data.videos_per_article,
        image_search_prompt: data.image_search_prompt ?? null,
      } as any)
      .eq("singleton", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Lightweight endpoint: persist only the system prompt (shared by manual,
// batch and scheduled generation flows).
export const saveSystemPrompt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      system_prompt: z.string().trim().min(20).max(8000),
      default_youtube_urls: z
        .array(z.string().trim().url().max(400))
        .max(50)
        .optional(),
      default_youtube_titles: z
        .array(z.string().trim().max(300))
        .max(50)
        .optional(),
      videos_per_article: z.number().int().min(0).max(5).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const update: Record<string, unknown> = { system_prompt: data.system_prompt };
    if (data.default_youtube_urls) update.default_youtube_urls = data.default_youtube_urls;
    if (data.default_youtube_titles) update.default_youtube_titles = data.default_youtube_titles;
    if (typeof data.videos_per_article === "number") update.videos_per_article = data.videos_per_article;
    const { error } = await context.supabase
      .from("blog_schedule_settings")
      .update(update as any)
      .eq("singleton", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Salva apenas o prompt do agente de SEO Local.
export const saveLocalSeoPrompt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      local_seo_system_prompt: z.string().trim().min(20).max(8000),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("blog_schedule_settings")
      .update({ local_seo_system_prompt: data.local_seo_system_prompt } as any)
      .eq("singleton", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// --- Pexels search proxy (admin-only) ---

// --- Sync de vídeos de canal/playlist do YouTube ---
export const syncYoutubeChannel = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      channelUrl: z.string().trim().url().max(400),
      limit: z.number().int().min(1).max(50).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { syncBlogChannel } = await import("./youtube-channel-sync.server");
    const result = await syncBlogChannel(context.supabase, data.channelUrl, data.limit ?? 50);
    if (!result.ok) throw new Error(result.error || "Falha ao sincronizar canal");
    return result;
  });

export const searchPexels = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ query: z.string().trim().min(2).max(120) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { searchPexelsImages } = await import("./pexels.server");
    const photos = await searchPexelsImages(data.query, 9);
    return photos;
  });

// --- Generate article now (manual trigger) ---

export const generatePostNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      keyword: z.string().trim().min(2).max(120).optional(),
      keywordIds: z.array(z.string().uuid()).max(8).optional(),
      topic: z.string().trim().max(400).optional(),
      youtubeUrls: z.array(z.string().trim().url().max(400)).max(50).default([]),
      coverImageOverride: z.string().trim().max(800).nullable().optional(),
      autoPublish: z.boolean().default(true),
      category: z.string().trim().min(2).max(60).optional(),
      linksToInsert: z.number().int().min(0).max(15).optional(),
      article_type: articleTypeSchema.optional(),
      ...productSchema,
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { runArticleGeneration } = await import("./blog-generator.server");
    return await runArticleGeneration(supabaseAdmin, {
      keyword: data.keyword,
      keywordIds: data.keywordIds,
      topic: data.topic,
      youtubeUrls: data.youtubeUrls,
      coverImageOverride: data.coverImageOverride ?? null,
      autoPublish: data.autoPublish,
      category: data.category,
      linksToInsert: data.linksToInsert,
      articleType: data.article_type,
      productOverride: {
        product_name: data.product_name ?? null,
        product_image: data.product_image ?? null,
        product_image_alt: data.product_image_alt ?? null,
        product_url: data.product_url ?? null,
        product_price: data.product_price ?? null,
        product_rating: data.product_rating ?? null,
        product_reviews_count: data.product_reviews_count ?? null,
        product_features: data.product_features ?? [],
        product_cta_label: data.product_cta_label ?? null,
        product_marketplace: data.product_marketplace ?? null,
      },
    });
  });

export const generatePostsBatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      count: z.number().int().min(1).max(10),
      keyword: z.string().trim().min(2).max(120).optional(),
      topic: z.string().trim().max(400).optional(),
      category: z.string().trim().min(2).max(60).optional(),
      linksToInsert: z.number().int().min(0).max(15).optional(),
      autoPublish: z.boolean().default(true),
      useLinkBuilding: z.boolean().default(true),
      article_type: articleTypeSchema.optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { runArticleGeneration } = await import("./blog-generator.server");

    const angles = [
      "guia completo para iniciantes — o que é, como funciona e por onde começar",
      "erros comuns a evitar e como corrigir cada um deles na prática",
      "checklist passo a passo para implementar do zero em 30 dias",
      "comparativo entre estratégias e qual escolher por porte de empresa",
      "estudo de caso e exemplos reais de resultados alcançados",
      "tendências, novidades e o que esperar nos próximos meses",
      "ferramentas, métricas e KPIs essenciais para acompanhar",
      "perguntas frequentes respondidas em profundidade",
      "estratégias avançadas para quem já tem o básico funcionando",
      "como contratar, terceirizar ou montar equipe interna",
    ];
    const shuffled = [...angles].sort(() => Math.random() - 0.5);

    const results: Array<{ id: string; title: string; ok: boolean; error?: string }> = [];
    for (let i = 0; i < data.count; i++) {
      try {
        const iterationTopic = data.keyword
          ? [data.topic, shuffled[i % shuffled.length]].filter(Boolean).join(" — ")
          : data.topic;
        const r = await runArticleGeneration(supabaseAdmin, {
          keyword: data.keyword,
          topic: iterationTopic,
          category: data.category,
          autoPublish: data.autoPublish,
          linksToInsert: data.useLinkBuilding ? data.linksToInsert : 0,
          articleType: data.article_type,
        });
        results.push({ id: r.id, title: r.title, ok: true });
      } catch (e) {
        results.push({ id: "", title: "", ok: false, error: e instanceof Error ? e.message : String(e) });
      }
    }
    return results;
  });

// Dispara manualmente o mesmo fluxo do cron externo (para testes ou
// adiantar geração de artigos). Respeita `posts_per_run` e `links_per_article`
// das configurações. Ignora janelas de horário/intervalo (força execução).
export const runScheduleNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ count: z.number().int().min(1).max(10).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { runArticleGeneration } = await import("./blog-generator.server");

    const { data: settings } = await supabaseAdmin
      .from("blog_schedule_settings")
      .select("posts_per_run, links_per_article, use_link_building")
      .eq("singleton", true)
      .maybeSingle();

    const runs = data.count ?? Math.max(1, Math.min(10, (settings as any)?.posts_per_run ?? 1));
    const useLB = (settings as any)?.use_link_building !== false;
    const links = useLB ? Math.max(0, Math.min(15, (settings as any)?.links_per_article ?? 3)) : 0;

    await supabaseAdmin
      .from("blog_schedule_settings")
      .update({ last_run_at: new Date().toISOString() } as any)
      .eq("singleton", true);

    const posts: Array<{ id: string; title: string }> = [];
    const errors: string[] = [];
    for (let i = 0; i < runs; i++) {
      try {
        const r = await runArticleGeneration(supabaseAdmin, { linksToInsert: links, fromSchedule: true });
        posts.push({ id: r.id, title: r.title });
      } catch (e) {
        errors.push((e as Error)?.message ?? String(e));
      }
    }
    return { generated: posts.length, posts, errors };
  });

// ───────── Services CRUD ─────────

export const listServices = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("services")
      .select("id, slug, label, description, icon, route_path, order_index, active, updated_at")
      .order("order_index", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string; slug: string; label: string; description: string; icon: string;
      route_path: string; order_index: number; active: boolean; updated_at: string;
    }>;
  });

export const saveService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      slug: z.string().trim().min(2).max(80).regex(/^[a-z0-9-]+$/, "use letras minúsculas, números e hífens"),
      label: z.string().trim().min(2).max(120),
      description: z.string().trim().max(400).default(""),
      icon: z.string().trim().max(20).default(""),
      route_path: z.string().trim().max(120).default(""),
      order_index: z.number().int().min(0).max(9999).default(0),
      active: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      slug: data.slug, label: data.label, description: data.description,
      icon: data.icon, route_path: data.route_path || `/${data.slug}`,
      order_index: data.order_index, active: data.active,
    };
    if (data.id) {
      const { error } = await (context.supabase as any).from("services").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await (context.supabase as any).from("services").insert(payload);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deleteService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any).from("services").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Videos ─────────

export const listAllVideos = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("blog_videos")
      .select("id, title, url, description, thumbnail_url, order_index, published, updated_at")
      .order("order_index", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string; title: string; url: string; description: string;
      thumbnail_url: string | null; order_index: number; published: boolean; updated_at: string;
    }>;
  });

export const saveVideo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      title: z.string().trim().min(2).max(200),
      url: z.string().trim().url().max(400),
      description: z.string().trim().max(800).default(""),
      thumbnail_url: z.string().trim().url().max(800).nullable().optional(),
      order_index: z.number().int().min(0).max(9999).default(0),
      published: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      title: data.title,
      url: data.url,
      description: data.description,
      thumbnail_url: data.thumbnail_url ?? null,
      order_index: data.order_index,
      published: data.published,
    };
    if (data.id) {
      const { error } = await (context.supabase as any).from("blog_videos").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await (context.supabase as any).from("blog_videos").insert({ ...payload, created_by: context.userId });
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deleteVideo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any).from("blog_videos").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Service Locations ─────────

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 120);
}

export const listAllLocations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("service_locations")
      .select("id, slug, service_slug, service_label, city, neighborhood, title, published, published_at, updated_at, body")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Array<any>;
    return rows.map((r) => ({
      id: r.id, slug: r.slug, service_slug: r.service_slug, service_label: r.service_label,
      city: r.city, neighborhood: r.neighborhood, title: r.title,
      published: r.published, published_at: r.published_at, updated_at: r.updated_at,
      body_length: typeof r.body === "string" ? r.body.length : 0,
    }));
  });

export const deleteLocation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any).from("service_locations").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const generateLocationPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      service_slug: z.string().trim().min(2).max(80),
      service_label: z.string().trim().min(2).max(120),
      city: z.string().trim().min(2).max(120),
      neighborhood: z.string().trim().max(120).optional().default(""),
      topic_instruction: z.string().trim().max(2000).optional().default(""),
      autoPublish: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { generateServiceLocationContent } = await import("./openai.server");
    const where = data.neighborhood
      ? `${data.neighborhood}, ${data.city}`
      : data.city;
    const baseSlug = slugify(`${data.service_label}-em-${where}`);
    const avoid = await fetchLocationAvoidSets(context.supabase, data.service_slug);
    const { data: settingsRow } = await (context.supabase as any)
      .from("blog_schedule_settings")
      .select("local_seo_system_prompt")
      .eq("singleton", true)
      .maybeSingle();
    const generated = await generateServiceLocationContent({
      serviceLabel: data.service_label,
      serviceSlug: data.service_slug,
      city: data.city,
      neighborhood: data.neighborhood || "",
      topicInstruction: data.topic_instruction || "",
      systemPrompt: settingsRow?.local_seo_system_prompt || "",
      avoidTitles: avoid.titles,
      avoidSnippets: avoid.snippets,
      avoidFaqQuestions: avoid.faqs,
    });
    // Ensure slug uniqueness
    let finalSlug = baseSlug;
    const { data: existing } = await (context.supabase as any)
      .from("service_locations")
      .select("id")
      .eq("slug", finalSlug)
      .maybeSingle();
    if (existing) finalSlug = `${baseSlug}-${Date.now().toString(36).slice(-5)}`;
    const insertPayload = {
      service_slug: data.service_slug,
      service_label: data.service_label,
      city: data.city,
      neighborhood: data.neighborhood || null,
      slug: finalSlug,
      title: generated.title,
      description: generated.description,
      body: generated.body,
      published: data.autoPublish,
      published_at: data.autoPublish ? new Date().toISOString() : null,
      created_by: context.userId,
    };
    const { data: row, error } = await (context.supabase as any)
      .from("service_locations")
      .insert(insertPayload)
      .select("id, slug, title, published")
      .single();
    if (error) throw new Error(error.message);
    return row as { id: string; slug: string; title: string; published: boolean };
  });

// Shared core: runs the batch loop, persisting input + progress and skipping
// locations that already exist so it can be safely re-run to resume a stopped lot.
async function runLocationsBatchCore(
  context: { supabase: any; userId: string },
  params: {
    service_slug: string;
    service_label: string;
    locations: string;
    topic_instruction: string;
    autoPublish: boolean;
  },
) {
  const { generateServiceLocationContent } = await import("./openai.server");
  const { data: settingsRow } = await (context.supabase as any)
    .from("blog_schedule_settings")
    .select("local_seo_system_prompt")
    .eq("singleton", true)
    .maybeSingle();
  const localSystemPrompt: string = settingsRow?.local_seo_system_prompt || "";
  const lines = params.locations
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const updateProgress = async (patch: Record<string, unknown>) => {
    try {
      await (context.supabase as any)
        .from("blog_schedule_settings")
        .update(patch)
        .eq("singleton", true);
    } catch {/* progress is best-effort */}
  };

  const startedAtIso = new Date().toISOString();
  const startedAtMs = Date.now();
  await updateProgress({
    local_seo_batch_status: "running",
    local_seo_batch_total: lines.length,
    local_seo_batch_done: 0,
    local_seo_batch_failed: 0,
    local_seo_batch_skipped: 0,
    local_seo_batch_processed: 0,
    local_seo_batch_current: lines[0] ?? null,
    local_seo_batch_started_at: startedAtIso,
    local_seo_batch_finished_at: null,
    local_seo_batch_last_error: null,
    local_seo_batch_input: params,
  });

  const results: Array<{ ok: boolean; city: string; neighborhood?: string; title?: string; error?: string; skipped?: boolean }> = [];
  let done = 0;
  let failed = 0;
  let skipped = 0;
  let processed = 0;
  let lastError: string | null = null;
  for (const line of lines) {
    await updateProgress({ local_seo_batch_current: line });
    try {
      const parts = line.split(/\s*[|\-–]\s*/);
      const city = (parts[0] || "").trim();
      const neighborhood = (parts[1] || "").trim();
      if (!city) { done++; await updateProgress({ local_seo_batch_done: done }); continue; }

      // Skip if a page for this service + city + neighborhood already exists,
      // so re-running the batch only fills in what's missing.
      const baseExists = (context.supabase as any)
        .from("service_locations")
        .select("id")
        .eq("service_slug", params.service_slug)
        .eq("city", city);
      const { data: dupe } = neighborhood
        ? await baseExists.eq("neighborhood", neighborhood).maybeSingle()
        : await baseExists.is("neighborhood", null).maybeSingle();
      if (dupe) {
        results.push({ ok: true, city, neighborhood, skipped: true });
        skipped++;
        done++;
        await updateProgress({ local_seo_batch_done: done, local_seo_batch_skipped: skipped });
        continue;
      }

      const where = neighborhood ? `${neighborhood}, ${city}` : city;
      const baseSlug = slugify(`${params.service_label}-em-${where}`);
      const avoid = await fetchLocationAvoidSets(context.supabase, params.service_slug);
      const generated = await generateServiceLocationContent({
        serviceLabel: params.service_label,
        serviceSlug: params.service_slug,
        city,
        neighborhood,
        topicInstruction: params.topic_instruction || "",
        systemPrompt: localSystemPrompt,
        avoidTitles: avoid.titles,
        avoidSnippets: avoid.snippets,
        avoidFaqQuestions: avoid.faqs,
      });
      let finalSlug = baseSlug;
      const { data: existing } = await (context.supabase as any)
        .from("service_locations")
        .select("id")
        .eq("slug", finalSlug)
        .maybeSingle();
      if (existing) finalSlug = `${baseSlug}-${Date.now().toString(36).slice(-5)}`;
      const { error } = await (context.supabase as any)
        .from("service_locations")
        .insert({
          service_slug: params.service_slug,
          service_label: params.service_label,
          city,
          neighborhood: neighborhood || null,
          slug: finalSlug,
          title: generated.title,
          description: generated.description,
          body: generated.body,
          published: params.autoPublish,
          published_at: params.autoPublish ? new Date().toISOString() : null,
          created_by: context.userId,
        });
      if (error) throw new Error(error.message);
      results.push({ ok: true, city, neighborhood, title: generated.title });
      processed++;
      await updateProgress({ local_seo_batch_processed: processed });
    } catch (e) {
      failed++;
      const msg = e instanceof Error ? e.message : String(e);
      lastError = msg;
      results.push({ ok: false, city: line, error: msg });
      await updateProgress({ local_seo_batch_last_error: msg.slice(0, 500) });
    } finally {
      done++;
      await updateProgress({ local_seo_batch_done: done, local_seo_batch_failed: failed });
    }
  }

  // Append a round entry to the execution history (keep last 20).
  const finishedAtIso = new Date().toISOString();
  const durationMs = Date.now() - startedAtMs;
  try {
    const { data: cur } = await (context.supabase as any)
      .from("blog_schedule_settings")
      .select("local_seo_batch_history")
      .eq("singleton", true)
      .maybeSingle();
    const prior: any[] = Array.isArray(cur?.local_seo_batch_history) ? cur.local_seo_batch_history : [];
    const entry = {
      started_at: startedAtIso,
      finished_at: finishedAtIso,
      duration_ms: durationMs,
      service_slug: params.service_slug,
      service_label: params.service_label,
      total: lines.length,
      processed,
      skipped,
      failed,
      last_error: lastError ? lastError.slice(0, 500) : null,
    };
    const nextHistory = [entry, ...prior].slice(0, 20);
    await updateProgress({ local_seo_batch_history: nextHistory });
  } catch {/* history is best-effort */}

  await updateProgress({
    local_seo_batch_status: "done",
    local_seo_batch_current: null,
    local_seo_batch_finished_at: finishedAtIso,
  });
  return results;
}

// Bulk generator: one line per location. Accepts "Cidade" or "Cidade | Bairro" or "Cidade - Bairro".
export const generateLocationsBatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      service_slug: z.string().trim().min(2).max(80),
      service_label: z.string().trim().min(2).max(120),
      locations: z.string().trim().min(2).max(20000),
      topic_instruction: z.string().trim().max(2000).optional().default(""),
      autoPublish: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    return runLocationsBatchCore(context, {
      service_slug: data.service_slug,
      service_label: data.service_label,
      locations: data.locations,
      topic_instruction: data.topic_instruction || "",
      autoPublish: data.autoPublish,
    });
  });

// Resume the last batch: re-runs with the saved input, skipping locations that
// already exist — so only the remaining (and previously failed) ones run.
export const continueLocationsBatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row } = await (context.supabase as any)
      .from("blog_schedule_settings")
      .select("local_seo_batch_input")
      .eq("singleton", true)
      .maybeSingle();
    const input = row?.local_seo_batch_input as
      | { service_slug: string; service_label: string; locations: string; topic_instruction?: string; autoPublish?: boolean }
      | null;
    if (!input || !input.locations) {
      throw new Error("Nenhum lote anterior encontrado para continuar.");
    }
    return runLocationsBatchCore(context, {
      service_slug: input.service_slug,
      service_label: input.service_label,
      locations: input.locations,
      topic_instruction: input.topic_instruction || "",
      autoPublish: input.autoPublish ?? true,
    });
  });

// Snapshot of the current bulk local-SEO generation, for UI polling.
export const getLocalSeoBatchProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data } = await (context.supabase as any)
      .from("blog_schedule_settings")
      .select("local_seo_batch_status, local_seo_batch_total, local_seo_batch_done, local_seo_batch_failed, local_seo_batch_skipped, local_seo_batch_processed, local_seo_batch_current, local_seo_batch_started_at, local_seo_batch_finished_at, local_seo_batch_last_error, local_seo_batch_history")
      .eq("singleton", true)
      .maybeSingle();
    return (data ?? null) as {
      local_seo_batch_status: string | null;
      local_seo_batch_total: number | null;
      local_seo_batch_done: number | null;
      local_seo_batch_failed: number | null;
      local_seo_batch_skipped: number | null;
      local_seo_batch_processed: number | null;
      local_seo_batch_current: string | null;
      local_seo_batch_started_at: string | null;
      local_seo_batch_finished_at: string | null;
      local_seo_batch_last_error: string | null;
      local_seo_batch_history: Array<{
        started_at: string;
        finished_at: string;
        duration_ms: number;
        service_slug: string;
        service_label: string;
        total: number;
        processed: number;
        skipped: number;
        failed: number;
        last_error: string | null;
      }> | null;
    } | null;
  });

// Regenerate the AI content of an existing service-location page (in place).
export const regenerateLocationPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error: getErr } = await (context.supabase as any)
      .from("service_locations")
      .select("id, service_slug, service_label, city, neighborhood")
      .eq("id", data.id)
      .maybeSingle();
    if (getErr) throw new Error(getErr.message);
    if (!row) throw new Error("Localidade não encontrada");

    const { generateServiceLocationContent } = await import("./openai.server");
    const { data: settingsRow } = await (context.supabase as any)
      .from("blog_schedule_settings")
      .select("local_seo_system_prompt")
      .eq("singleton", true)
      .maybeSingle();
    const avoid = await fetchLocationAvoidSets(context.supabase, row.service_slug);
    const generated = await generateServiceLocationContent({
      serviceLabel: row.service_label,
      serviceSlug: row.service_slug,
      city: row.city,
      neighborhood: row.neighborhood || "",
      topicInstruction: "",
      systemPrompt: settingsRow?.local_seo_system_prompt || "",
      avoidTitles: avoid.titles,
      avoidSnippets: avoid.snippets,
      avoidFaqQuestions: avoid.faqs,
    });
    const { error: updErr } = await (context.supabase as any)
      .from("service_locations")
      .update({
        title: generated.title,
        description: generated.description,
        body: generated.body,
      })
      .eq("id", data.id);
    if (updErr) throw new Error(updErr.message);
    return { ok: true, id: data.id, title: generated.title, length: generated.body.length };
  });

// Regenerate every published location whose body is suspiciously thin
// (likely a truncated previous generation that renders nearly blank).
export const regenerateThinLocations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ minLength: z.number().int().min(100).max(5000).default(800) }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: rows, error } = await (context.supabase as any)
      .from("service_locations")
      .select("id, slug, title, body")
      .eq("published", true);
    if (error) throw new Error(error.message);
    const thin = (rows ?? []).filter((r: any) => !r.body || String(r.body).trim().length < data.minLength);
    const results: Array<{ id: string; slug: string; ok: boolean; error?: string }> = [];
    for (const r of thin) {
      try {
        const { generateServiceLocationContent } = await import("./openai.server");
        const { data: settingsRow } = await (context.supabase as any)
          .from("blog_schedule_settings")
          .select("local_seo_system_prompt")
          .eq("singleton", true)
          .maybeSingle();
        const { data: full } = await (context.supabase as any)
          .from("service_locations")
          .select("id, service_slug, service_label, city, neighborhood")
          .eq("id", r.id)
          .maybeSingle();
        if (!full) throw new Error("não encontrada");
        const avoid = await fetchLocationAvoidSets(context.supabase, full.service_slug);
        const generated = await generateServiceLocationContent({
          serviceLabel: full.service_label,
          serviceSlug: full.service_slug,
          city: full.city,
          neighborhood: full.neighborhood || "",
          topicInstruction: "",
          systemPrompt: settingsRow?.local_seo_system_prompt || "",
          avoidTitles: avoid.titles,
          avoidSnippets: avoid.snippets,
          avoidFaqQuestions: avoid.faqs,
        });
        const { error: updErr } = await (context.supabase as any)
          .from("service_locations")
          .update({
            title: generated.title,
            description: generated.description,
            body: generated.body,
          })
          .eq("id", r.id);
        if (updErr) throw new Error(updErr.message);
        results.push({ id: r.id, slug: r.slug, ok: true });
      } catch (e) {
        results.push({ id: r.id, slug: r.slug, ok: false, error: e instanceof Error ? e.message : String(e) });
      }
    }
    return { scanned: rows?.length ?? 0, thin: thin.length, regenerated: results.filter((r) => r.ok).length, results };
  });

// ───────── Image upload (admin → private bucket served via /api/public/post-images) ─────────

export const uploadPostImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      fileName: z.string().trim().min(1).max(200),
      contentType: z.string().trim().min(3).max(80),
      base64: z.string().min(10).max(15_000_000), // ~10 MB
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const safeName = data.fileName.toLowerCase().replace(/[^a-z0-9.\-_]/g, "-").slice(-60);
    const path = `${context.userId}/${Date.now()}-${safeName}`;
    const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
    const { error } = await supabaseAdmin.storage
      .from("post-images")
      .upload(path, bytes, { contentType: data.contentType, upsert: false });
    if (error) throw new Error(error.message);
    return { url: `/api/public/post-images/${path}`, path };
  });

// ───────── Users & roles ─────────

const ROLE_VALUES = ["admin", "editor", "user"] as const;
type AppRole = (typeof ROLE_VALUES)[number];

export const listAllUsers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: list, error } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
    if (error) throw new Error(error.message);
    const userIds = list.users.map((u) => u.id);
    const { data: roles, error: rolesErr } = await (supabaseAdmin as any)
      .from("user_roles")
      .select("user_id, role")
      .in("user_id", userIds.length ? userIds : ["00000000-0000-0000-0000-000000000000"]);
    if (rolesErr) throw new Error(rolesErr.message);
    const byUser = new Map<string, AppRole[]>();
    for (const r of roles ?? []) {
      const arr = byUser.get(r.user_id) ?? [];
      arr.push(r.role as AppRole);
      byUser.set(r.user_id, arr);
    }
    return list.users
      .map((u) => {
        const userRoles = byUser.get(u.id) ?? [];
        const approved = userRoles.length > 0;
        return {
          id: u.id,
          email: u.email ?? "",
          created_at: u.created_at,
          last_sign_in_at: u.last_sign_in_at ?? null,
          email_confirmed_at: u.email_confirmed_at ?? null,
          roles: userRoles,
          approved,
        };
      })
      .sort((a, b) => (a.approved === b.approved ? 0 : a.approved ? 1 : -1));
  });

export const setUserRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      user_id: z.string().uuid(),
      role: z.enum(ROLE_VALUES),
      grant: z.boolean(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (data.grant) {
      const { error } = await (supabaseAdmin as any)
        .from("user_roles")
        .upsert({ user_id: data.user_id, role: data.role }, { onConflict: "user_id,role" });
      if (error) throw new Error(error.message);
    } else {
      // Prevent removing your own admin role to avoid lockout
      if (data.user_id === context.userId && data.role === "admin") {
        throw new Error("Você não pode remover seu próprio acesso de administrador.");
      }
      const { error } = await (supabaseAdmin as any)
        .from("user_roles")
        .delete()
        .eq("user_id", data.user_id)
        .eq("role", data.role);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deleteUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ user_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    if (data.user_id === context.userId) {
      throw new Error("Você não pode excluir sua própria conta por aqui.");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.user_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Page SEO overrides ─────────

export const listPageSeo = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("page_seo")
      .select("path, title, description, updated_at");
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{ path: string; title: string | null; description: string | null; updated_at: string }>;
  });

export const savePageSeo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      path: z.string().trim().min(1).max(200),
      title: z.string().trim().max(200).nullable().optional(),
      description: z.string().trim().max(400).nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      path: data.path,
      title: data.title ?? null,
      description: data.description ?? null,
      updated_at: new Date().toISOString(),
      updated_by: context.userId,
    };
    const { error } = await (context.supabase as any)
      .from("page_seo")
      .upsert(payload, { onConflict: "path" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
// ───────── Páginas Pilar (Top 6 e variações) ─────────

const pillarItemSchema = z.object({
  position: z.number().int().min(0).max(50).optional(),
  name: z.string().trim().max(200).optional(),
  brand: z.string().trim().max(120).optional(),
  image: z.string().trim().max(800).optional(),
  image_alt: z.string().trim().max(200).optional(),
  rating: z.number().min(0).max(5).optional(),
  short_desc: z.string().trim().max(500).optional(),
  best_for: z.string().trim().max(300).optional(),
  price_from: z.string().trim().max(60).optional(),
  pros: z.array(z.string().trim().min(1).max(200)).max(12).optional(),
  cons: z.array(z.string().trim().min(1).max(200)).max(12).optional(),
  features: z.array(z.string().trim().min(1).max(200)).max(12).optional(),
  affiliate_url: z.string().trim().max(800).optional(),
  affiliate_label: z.string().trim().max(80).optional(),
  marketplace: z.string().trim().max(60).optional(),
  review_url: z.string().trim().max(400).optional(),
  paragraph: z.string().trim().max(3000).optional(),
});

const pillarInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().min(2).max(120).regex(/^[a-z0-9-]+$/),
  title: z.string().trim().min(3).max(200),
  subtitle: z.string().trim().max(300).nullable().optional(),
  intro: z.string().trim().max(4000).nullable().optional(),
  eyebrow: z.string().trim().max(80).nullable().optional(),
  meta_title: z.string().trim().max(200).nullable().optional(),
  meta_description: z.string().trim().max(400).nullable().optional(),
  hero_image: z.string().trim().max(800).nullable().optional(),
  conclusion: z.string().trim().max(4000).nullable().optional(),
  items: z.array(pillarItemSchema).max(20).default([]),
  published: z.boolean().default(true),
});

export const listPillarPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("pillar_pages")
      .select("id, slug, title, subtitle, published, updated_at, items")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({
      id: r.id, slug: r.slug, title: r.title, subtitle: r.subtitle,
      published: r.published, updated_at: r.updated_at,
      items_count: Array.isArray(r.items) ? r.items.length : 0,
    }));
  });

export const getPillarForEdit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await (context.supabase as any)
      .from("pillar_pages")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

export const savePillarPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => pillarInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      slug: data.slug,
      title: data.title,
      subtitle: data.subtitle ?? null,
      intro: data.intro ?? null,
      eyebrow: data.eyebrow ?? null,
      meta_title: data.meta_title ?? null,
      meta_description: data.meta_description ?? null,
      hero_image: data.hero_image ?? null,
      conclusion: data.conclusion ?? null,
      items: data.items,
      published: data.published,
    };
    if (data.id) {
      const { error } = await (context.supabase as any).from("pillar_pages").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await (context.supabase as any).from("pillar_pages").insert(payload);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const deletePillarPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any).from("pillar_pages").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const generatePillarFromBrief = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      slug: z.string().trim().min(1).max(120),
      brief: z.string().trim().min(20).max(8000),
      itemsCount: z.number().int().min(3).max(10).default(6),
      productSummaries: z.string().trim().max(8000).default(""),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { generatePillarPage } = await import("./openai.server");
    const generated = await generatePillarPage({
      brief: data.brief,
      productSummaries: data.productSummaries,
      itemsCount: data.itemsCount,
    });
    return generated;
  });

// ───────── Scrape product from marketplace URL (Firecrawl) ─────────

export const scrapeProductFromUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      url: z.string().trim().url().max(2000),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const apiKey = process.env.FIRECRAWL_API_KEY;
    if (!apiKey) throw new Error("FIRECRAWL_API_KEY não configurada");

    const host = (() => { try { return new URL(data.url).hostname.toLowerCase(); } catch { return ""; } })();
    const marketplace = host.includes("amazon") ? "Amazon"
      : host.includes("mercadolivre") || host.includes("mercadolibre") ? "Mercado Livre"
      : host.includes("shopee") ? "Shopee"
      : host.includes("magazineluiza") || host.includes("magalu") ? "Magazine Luiza"
      : host.includes("americanas") ? "Americanas"
      : "";

    const schema = {
      type: "object",
      properties: {
        title: { type: "string", description: "Nome/título completo do produto" },
        image: { type: "string", description: "URL absoluta da imagem principal de alta resolução do produto" },
        price: { type: "string", description: "Preço atual com moeda, ex: R$ 1.299,00" },
        rating: { type: "number", description: "Nota média de avaliação (0-5)" },
        reviews_count: { type: "number", description: "Quantidade total de avaliações/depoimentos" },
        features: {
          type: "array",
          items: { type: "string" },
          description: "Características/bullet points principais do produto (máx 8)",
        },
        description: { type: "string", description: "Descrição longa do produto em texto corrido (200-600 palavras)" },
      },
      required: ["title"],
    };

    const fcRes = await fetch("https://api.firecrawl.dev/v2/scrape", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        url: data.url,
        onlyMainContent: true,
        formats: [{
          type: "json",
          schema,
          prompt: `Extraia os dados do produto desta página de marketplace${marketplace ? ` (${marketplace})` : ""}. Use a imagem principal em alta resolução. Para features, liste 4 a 8 bullet points objetivos. Para description, escreva texto corrido em português do Brasil.`,
        }],
      }),
    });

    if (!fcRes.ok) {
      const txt = await fcRes.text().catch(() => "");
      throw new Error(`Firecrawl falhou (${fcRes.status}): ${txt.slice(0, 200)}`);
    }
    const fcJson: any = await fcRes.json();
    const extracted = fcJson?.data?.json ?? fcJson?.json ?? {};

    // Upload da imagem para o bucket (se houver)
    let imageUrl: string | null = null;
    const remoteImg: string | undefined = extracted?.image;
    if (remoteImg && typeof remoteImg === "string" && /^https?:\/\//i.test(remoteImg)) {
      try {
        const imgRes = await fetch(remoteImg, { headers: { "User-Agent": "Mozilla/5.0" } });
        if (imgRes.ok) {
          const ct = imgRes.headers.get("content-type") || "image/jpeg";
          const buf = new Uint8Array(await imgRes.arrayBuffer());
          if (buf.byteLength > 0 && buf.byteLength < 10_000_000) {
            const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
            const ext = ct.includes("png") ? "png" : ct.includes("webp") ? "webp" : "jpg";
            const path = `${context.userId}/${Date.now()}-scraped.${ext}`;
            const { error } = await supabaseAdmin.storage
              .from("post-images")
              .upload(path, buf, { contentType: ct, upsert: false });
            if (!error) imageUrl = `/api/public/post-images/${path}`;
          }
        }
      } catch { /* mantém remoteImg como fallback */ }
      if (!imageUrl) imageUrl = remoteImg;
    }

    const features: string[] = Array.isArray(extracted?.features)
      ? extracted.features.map((f: any) => String(f).trim()).filter(Boolean).slice(0, 8)
      : [];

    return {
      title: typeof extracted?.title === "string" ? extracted.title.trim() : "",
      image: imageUrl,
      price: typeof extracted?.price === "string" ? extracted.price.trim() : "",
      rating: typeof extracted?.rating === "number" ? extracted.rating : null,
      reviews_count: typeof extracted?.reviews_count === "number" ? Math.round(extracted.reviews_count) : null,
      features,
      description: typeof extracted?.description === "string" ? extracted.description.trim() : "",
      marketplace,
    };
  });

// ───────── Product models (admin) ─────────

const productModelInput = z.object({
  id: z.string().uuid().optional().nullable(),
  slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9-]+$/),
  name: z.string().trim().min(1).max(200),
  brand: z.string().trim().max(120).nullable().optional(),
  short_desc: z.string().trim().max(400).nullable().optional(),
  long_intro: z.array(z.string().trim().max(1500)).max(10).default([]),
  pros: z.array(z.string().trim().max(200)).max(15).default([]),
  cons: z.array(z.string().trim().max(200)).max(15).default([]),
  best_for: z.array(z.string().trim().max(200)).max(10).default([]),
  not_ideal_for: z.array(z.string().trim().max(200)).max(10).default([]),
  specs: z.array(z.object({ label: z.string().trim().max(80), value: z.string().trim().max(200) })).max(20).default([]),
  faq: z.array(z.object({ q: z.string().trim().max(200), a: z.string().trim().max(1200) })).max(15).default([]),
  highlights: z.array(z.string().trim().max(200)).max(10).default([]),
  image_url: z.string().trim().max(800).nullable().optional(),
  image_alt: z.string().trim().max(300).nullable().optional(),
  price_from_brl: z.number().nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  reviews_count: z.number().int().nullable().optional(),
  affiliate_amazon: z.string().trim().max(1500).nullable().optional(),
  affiliate_mercadolivre: z.string().trim().max(1500).nullable().optional(),
  affiliate_magalu: z.string().trim().max(1500).nullable().optional(),
  affiliate_primary_url: z.string().trim().max(1500).nullable().optional(),
  affiliate_marketplace: z.string().trim().max(120).nullable().optional(),
  affiliate_cta_label: z.string().trim().max(80).nullable().optional(),
  ranking_position: z.number().int().min(0).max(99).nullable().optional(),
  active: z.boolean().default(true),
  meta_title: z.string().trim().max(200).nullable().optional(),
  meta_description: z.string().trim().max(400).nullable().optional(),
  spec_brief: z.string().trim().max(8000).nullable().optional(),
});

export const listProductModels = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await (context.supabase as any)
      .from("product_models")
      .select("id, slug, name, brand, ranking_position, active, image_url, rating, updated_at")
      .order("ranking_position", { ascending: true, nullsFirst: false })
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getProductModelForEdit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await (context.supabase as any)
      .from("product_models").select("*").eq("id", data.id).maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

export const saveProductModel = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => productModelInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { id, ...rest } = data;
    const payload: Record<string, unknown> = {
      ...rest,
      brand: rest.brand ?? null,
      short_desc: rest.short_desc ?? null,
      image_url: rest.image_url ?? null,
      image_alt: rest.image_alt ?? null,
      price_from_brl: rest.price_from_brl ?? null,
      rating: rest.rating ?? null,
      reviews_count: rest.reviews_count ?? null,
      affiliate_amazon: rest.affiliate_amazon ?? null,
      affiliate_mercadolivre: rest.affiliate_mercadolivre ?? null,
      affiliate_magalu: rest.affiliate_magalu ?? null,
      affiliate_primary_url: rest.affiliate_primary_url ?? null,
      affiliate_marketplace: rest.affiliate_marketplace ?? null,
      affiliate_cta_label: rest.affiliate_cta_label ?? null,
      ranking_position: rest.ranking_position ?? null,
      meta_title: rest.meta_title ?? null,
      meta_description: rest.meta_description ?? null,
      spec_brief: rest.spec_brief ?? null,
    };
    if (id) {
      const { error } = await (context.supabase as any).from("product_models").update(payload).eq("id", id);
      if (error) throw new Error(error.message);
      return { ok: true, id };
    }
    const { data: inserted, error } = await (context.supabase as any).from("product_models").insert(payload).select("id").single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted.id };
  });

export const deleteProductModel = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await (context.supabase as any).from("product_models").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const generateProductModelFromBrief = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      name: z.string().trim().min(1).max(200),
      brand: z.string().trim().max(120).optional(),
      brief: z.string().trim().min(20).max(8000),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { generateProductModel } = await import("./openai.server");
    return await generateProductModel({ name: data.name, brand: data.brand, brief: data.brief });
  });

// ═══════════════════════════════════════════════════════════════
// App Secrets (API Keys editáveis em runtime)
// ═══════════════════════════════════════════════════════════════

export const APP_SECRET_NAMES = [
  "OPENAI_API_KEY",
  "PEXELS_API_KEY",
] as const;

const appSecretNameSchema = z.enum(APP_SECRET_NAMES);

export const APP_SECRET_META: Record<
  (typeof APP_SECRET_NAMES)[number],
  { label: string; where: string }
> = {
  OPENAI_API_KEY: {
    label: "OpenAI API Key",
    where: "Gere em platform.openai.com/api-keys (chave sk-...).",
  },
  PEXELS_API_KEY: {
    label: "Pexels API Key",
    where: "Gere em pexels.com/api/new — usada para banco de imagens dos posts.",
  },
};

export const listAppSecrets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getAppSecret, getAppSecretSource, maskSecretValue } = await import(
      "@/lib/app-secrets.server"
    );
    const { data } = await (supabaseAdmin as any)
      .from("app_secrets")
      .select("name, updated_at");
    const rowsByName = new Map<string, { updated_at: string }>();
    for (const r of (data ?? []) as Array<{ name: string; updated_at: string }>) {
      rowsByName.set(r.name, { updated_at: r.updated_at });
    }
    const items = await Promise.all(
      APP_SECRET_NAMES.map(async (name) => {
        const source = await getAppSecretSource(name);
        const value = await getAppSecret(name);
        return {
          name,
          label: APP_SECRET_META[name].label,
          where: APP_SECRET_META[name].where,
          configured: !!value,
          source,
          masked: maskSecretValue(value),
          updated_at: rowsByName.get(name)?.updated_at ?? null,
        };
      }),
    );
    return items;
  });

export const saveAppSecret = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      name: appSecretNameSchema,
      value: z.string().trim().min(1).max(4000),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { invalidateAppSecret } = await import("@/lib/app-secrets.server");
    const { error } = await (supabaseAdmin as any)
      .from("app_secrets")
      .upsert(
        { name: data.name, value: data.value, updated_by: context.userId },
        { onConflict: "name" },
      );
    if (error) throw new Error(error.message);
    invalidateAppSecret(data.name);
    return { ok: true };
  });

export const deleteAppSecret = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ name: appSecretNameSchema }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { invalidateAppSecret } = await import("@/lib/app-secrets.server");
    const { error } = await (supabaseAdmin as any)
      .from("app_secrets")
      .delete()
      .eq("name", data.name);
    if (error) throw new Error(error.message);
    invalidateAppSecret(data.name);
    return { ok: true };
  });

